import { Injectable } from '@angular/core';

// Razorpay checkout is loaded on demand instead of from index.html - as a blocking <script> in
// <head> it held up every page (blank screen until Razorpay's CDN answered) and pulled in ~30
// payment-method files on pages that never take a payment.
const RAZORPAY_CHECKOUT_SRC = 'https://checkout.razorpay.com/v1/checkout.js';
const LOAD_TIMEOUT_MS = 20000;

@Injectable({ providedIn: 'root' })
export class RazorpayLoaderService {

    private loading: Promise<any> | null = null;

    // Resolves with window.Razorpay. Safe to call any number of times - the script is only
    // requested once; a failed/timed-out attempt is forgotten so the next call retries.
    load(): Promise<any> {
        const win = window as any;
        if (win.Razorpay) return Promise.resolve(win.Razorpay);
        if (this.loading) return this.loading;

        this.loading = new Promise((resolve, reject) => {
            const script = document.createElement('script');
            let settled = false;
            let timer: any;

            const finish = (ok: boolean) => {
                if (settled) return;
                settled = true;
                clearTimeout(timer);
                if (ok && win.Razorpay) {
                    resolve(win.Razorpay);
                } else {
                    this.loading = null;
                    reject(new Error('Razorpay checkout failed to load'));
                }
            };

            script.src = RAZORPAY_CHECKOUT_SRC;
            script.async = true;
            script.onload = () => finish(true);
            script.onerror = () => finish(false);
            timer = setTimeout(() => finish(false), LOAD_TIMEOUT_MS);
            document.head.appendChild(script);
        });
        return this.loading;
    }

    // Fire-and-forget warm-up for when a payment flow opens, so the script is usually ready
    // by the time the user presses Pay. Errors are ignored here - load() reports them on Pay.
    preload(): void {
        this.load().catch(() => { });
    }
}
